<template>
  <div
    class="flex flex-col justify-center items-center fixed z-50 inset-0 bg-opacity-25 bg-black"
    @click.self="close"
  >
    <div class="form-card">
      <h3 class="font-display font-semibold text-lg text-content-700">
        {{ mode === 'renew' ? 'Registrar pago en efectivo y reactivar' : 'Convertir a plan en efectivo' }}
      </h3>
      <p class="text-sm text-content-400 mt-1">
        Plan #{{ quote.id }}
      </p>

      <div class="flex flex-col gap-4 mt-4">
        <div class="form-field">
          <label for="cash_payment_frequency">Frecuencia de pago</label>
          <select id="cash_payment_frequency" v-model="form.payment_frequency_id">
            <option :value="null">Seleccionar</option>
            <option
              v-for="frequency in selectablePaymentFrequencies"
              :key="frequency.id"
              :value="frequency.id"
            >
              {{ frequency.name }} - {{ frequency.weeks }} {{ frequency.weeks === 1 ? 'semana' : 'semanas' }}
            </option>
          </select>
          <p v-if="isCourtesySource" class="text-xs text-tertiary-600 mt-1">
            Este plan estaba en cortesía. Selecciona una frecuencia de pago real para empezar a cobrar.
          </p>
        </div>

        <div class="form-field">
          <label for="cash_delivery_frequency">Frecuencia de entrega</label>
          <select id="cash_delivery_frequency" v-model="form.delivery_frequency_id">
            <option :value="null">Seleccionar</option>
            <option
              v-for="frequency in deliveryFrequencies"
              :key="frequency.id"
              :value="frequency.id"
            >
              {{ frequency.name }} - {{ frequency.weeks }} {{ frequency.weeks === 1 ? 'semana' : 'semanas' }}
            </option>
          </select>
        </div>

        <div class="form-field">
          <label for="cash_cycles">Ciclos prepagados</label>
          <input
            id="cash_cycles"
            type="number"
            min="1"
            step="1"
            v-model.number="form.cycles"
          />
        </div>

        <div class="form-field">
          <label for="cash_amount">Monto recibido</label>
          <input
            id="cash_amount"
            type="number"
            min="0"
            step="0.01"
            v-model.number="form.amount"
          />
        </div>

        <div class="form-field">
          <label for="cash_notes">Notas (opcional)</label>
          <textarea
            id="cash_notes"
            rows="2"
            v-model="form.notes"
            placeholder="Referencia, comentarios..."
          ></textarea>
        </div>

        <div class="rounded-xl bg-tertiary-100 px-4 py-2 text-sm text-tertiary-700">
          <div class="flex justify-between">
            <span>Total esperado</span>
            <span class="font-semibold">{{ integerToCurrency(expectedTotal) }}</span>
          </div>
          <template v-if="couponFirstCycleDiscount > 0">
            <div class="flex justify-between mt-1">
              <span>Cupón {{ quote.coupon.code }} (solo primer ciclo)</span>
              <span class="font-semibold">- {{ integerToCurrency(couponFirstCycleDiscount) }}</span>
            </div>
            <div class="flex justify-between mt-1">
              <span>Total esperado con cupón</span>
              <span class="font-semibold">{{ integerToCurrency(expectedTotalWithCoupon) }}</span>
            </div>
          </template>
          <p class="text-xs text-tertiary-600 mt-1">
            {{ cycleCount }} ciclo{{ cycleCount === 1 ? '' : 's' }} × {{ integerToCurrency(perCycleTotal) }}
            (referencia; el monto recibido es libre).
          </p>
        </div>
      </div>

      <primary-button
        class="w-auto mt-6"
        :loading="loading"
        :disabled="!canConfirm"
        @buttonClicked="confirm"
        >Confirmar
      </primary-button>

      <button
        v-if="!loading"
        class="link mt-4 mx-auto w-full"
        @click.prevent="close"
      >
        Cancelar
      </button>
    </div>
  </div>
</template>

<script>
import { integerToCurrency } from "@/utils/currencyFormatter";

export default {
  name: "CashPaymentModal",

  props: {
    quote: {
      type: Object,
      required: true,
    },
    // 'activate' (convert active card plan) | 'renew' (reactivate suspended cash plan)
    mode: {
      type: String,
      default: "activate",
    },
    paymentFrequencies: {
      type: Array,
      default: () => [],
    },
    deliveryFrequencies: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: {
        // A courtesy plan sits on the hidden "Cortesía" frequency; never carry it
        // over — force a fresh, real selection instead of defaulting to it.
        payment_frequency_id: this.quote.courtesy
          ? null
          : this.quote.payment_frequency_id || null,
        delivery_frequency_id: this.quote.delivery_frequency_id || null,
        cycles: 1,
        amount: null,
        notes: "",
      },
    };
  },

  computed: {
    // The plan is graduating out of courtesy (free) into a paying cash plan.
    isCourtesySource() {
      return !!this.quote.courtesy;
    },

    // Hide the inactive "Cortesía" frequency (active=0) — it is not a real,
    // chargeable cadence and must never back a cash plan.
    selectablePaymentFrequencies() {
      return (this.paymentFrequencies || []).filter(
        (frequency) => Number(frequency.active) !== 0
      );
    },

    // Per-cycle total mirrors the plan-detail "Total" line (total_price - discount).
    perCycleTotal() {
      const total = Number(this.quote.total_price || 0);
      const discount = Number(this.quote.discount_amount || 0);
      return Math.max(0, total - discount);
    },

    cycleCount() {
      const n = Number(this.form.cycles);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
    },

    expectedTotal() {
      return this.perCycleTotal * this.cycleCount;
    },

    // Mirrors CashPaymentService::record: the attached coupon discounts ONE
    // cycle's worth of the expected floor, and only when this would be the
    // plan's FIRST successful payment (never on renewals).
    couponFirstCycleDiscount() {
      const coupon = this.quote.coupon;
      if (
        !coupon ||
        this.quote.has_successful_plan_payment ||
        this.quote.coupon_redemption
      ) {
        return 0;
      }

      const perCycle = this.perCycleTotal;
      const raw =
        coupon.discount_type === "percentage"
          ? (perCycle * Number(coupon.discount_value)) / 100
          : Number(coupon.discount_value);

      return Math.min(Math.max(raw, 0), perCycle);
    },

    expectedTotalWithCoupon() {
      return Math.max(0, this.expectedTotal - this.couponFirstCycleDiscount);
    },

    canConfirm() {
      return (
        this.cycleCount >= 1 &&
        Number(this.form.amount) > 0 &&
        // Converting a courtesy plan requires a real payment frequency.
        (!this.isCourtesySource || !!this.form.payment_frequency_id) &&
        !this.loading
      );
    },
  },

  methods: {
    integerToCurrency,

    close() {
      if (this.loading) return;
      this.$emit("close");
    },

    confirm() {
      if (!this.canConfirm) return;

      this.$emit("confirm", {
        cycles: this.cycleCount,
        amount: Number(this.form.amount),
        payment_frequency_id: this.form.payment_frequency_id,
        delivery_frequency_id: this.form.delivery_frequency_id,
        notes: this.form.notes || null,
      });
    },
  },
};
</script>
